'use client';

import Image from 'next/image';
import {
  SyntheticEvent,
  FC,
  memo,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { useLazyLoad, useVideoAutoplay } from '@/src/hooks/useInView';

import { LimitedTimer } from './LimitedTimer';
import { PromoLabel } from '@/src/components/PromoLabel';

// Типы для элементов, которые могут содержать промо
interface PromoItem {
  __typename?:
    | 'Style'
    | 'PhotoSession'
    | 'Effect'
    | 'CombiVideo'
    | 'StoryLine'
    | 'VideoLook'
    | 'VideoStyle'
    | 'VideoVeo3';
  id: string;
}

const styles = {
  styleItem: 'shrink-0',
  wrapper:
    ' w-[152px] relative overflow-hidden rounded-lg z-0 will-change-transform',
  actual: '!w-[220px]',
  best: 'border-1 border-white',
  limitedYellow: 'border-1 border-yellowFade80',
  limitedRose: 'border-1 border-roseFade80',
  video: 'aspect-4/5',
  preview:
    'w-full h-auto aspect-square object-cover object-top pointer-events-none relative z-[-1]',
  previewVideo: 'aspect-4/5',
  tag: 'absolute top-1 right-1',
  gradient: 'absolute inset-0 bg-gradient-sm z-10',
  gradientActual: 'bg-gradient-xxl',
  info: 'absolute left-3 bottom-3 z-10',
  styleName: 'text-sm font-medium text-left',
  skeleton: 'absolute z-30 inset-0 animate-skeleton-pulse',
  placeholder: 'absolute inset-0 bg-gray-200 animate-pulse',
};

type TPackPreviewProps = {
  id: string;
  categoryId?: string;
  index: number;
  categoryIndex?: number;
  isNew?: boolean;
  isPopular?: boolean;
  image: string;
  name?: string;
  handleClickImage: (event: SyntheticEvent) => void;
  isVideo?: boolean;
  isActual?: boolean;
  isBest?: boolean;
  isTrending?: boolean;
  isLimited?: boolean;
  isVeo3?: boolean;
  limitedUntil?: Date;
  // Добавляем данные для PromoLabel
  __typename?:
    | 'Style'
    | 'PhotoSession'
    | 'Effect'
    | 'CombiVideo'
    | 'StoryLine'
    | 'VideoLook'
    | 'VideoStyle'
    | 'VideoVeo3';
};

export const PackPreview: FC<TPackPreviewProps> = memo(
  ({
    id,
    categoryId,
    isNew,
    isPopular,
    image,
    index,
    name,
    handleClickImage,
    isVideo,
    isActual,
    isBest,
    isTrending,
    isLimited,
    limitedUntil,
    isVeo3,
    __typename,
  }) => {
    // Состояния
    const [mediaLoaded, setMediaLoaded] = useState(false);
    const [isTimerLoaded, setIsTimerLoaded] = useState(false);
    const [isLeft30mins, setIsLeft30mins] = useState(false);

    // Мемоизированные вычисления
    const isPriority = useMemo(
      () => (isTrending || isBest || isLimited) && index < 3,
      [isTrending, isBest, isLimited, index],
    );

    // Хуки для ленивой загрузки и автоплея
    const [setLazyRef, shouldLoad] = useLazyLoad({
      rootMargin: isPriority ? '50px' : '200px', // Приоритетные элементы загружаются раньше
      threshold: 0,
    });

    const [setVideoRef] = useVideoAutoplay({
      rootMargin: '0px',
      threshold: 0.3, // Видео играет когда видно на 30%
    });

    // Refs для элементов
    const buttonRef = useRef<HTMLButtonElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Объединяем refs
    const setElementRef = useCallback(
      (node: HTMLButtonElement | null) => {
        buttonRef.current = node;
        setLazyRef(node);
      },
      [setLazyRef],
    );

    const setVideoElementRef = useCallback(
      (node: HTMLVideoElement | null) => {
        videoRef.current = node;
        setVideoRef(node);
      },
      [setVideoRef],
    );

    // Мемоизированные стили
    const containerClasses = useMemo(() => {
      const showBorder = (isLimited && isTimerLoaded) || mediaLoaded;

      return twMerge(
        styles.wrapper,
        isActual && styles.actual,
        isVideo && !isBest && !isTrending && !isLimited && styles.video,
        isBest && styles.best,
        isLimited && styles.limitedYellow,
        isLimited && isLeft30mins && styles.limitedRose,
        !showBorder && 'border-none',
        isVeo3 && 'aspect-[16/9] w-[280px]',
      );
    }, [
      isLimited,
      isTimerLoaded,
      mediaLoaded,
      isActual,
      isVideo,
      isBest,
      isTrending,
      isLeft30mins,
      isVeo3,
    ]);

    const previewClasses = useMemo(
      () =>
        twMerge(
          styles.preview,
          isVideo &&
            !isBest &&
            !isTrending &&
            !isLimited &&
            styles.previewVideo,
          isVeo3 && 'block h-full w-full',
        ),
      [isVideo, isBest, isTrending, isLimited, isVeo3],
    );

    // Коллбэки
    const handleMediaLoad = useCallback(() => {
      setMediaLoaded(true);
    }, []);

    const handleTimerChange = useCallback((is30mins: boolean) => {
      setIsLeft30mins(is30mins);
    }, []);

    const handleTimerLoad = useCallback(() => {
      setIsTimerLoaded(true);
    }, []);

    // Мемоизированные компоненты
    const TagComponent = useMemo(() => {
      if (isLimited || !shouldLoad) {
        return null;
      }

      if (isNew) {
        return (
          <Image
            width={42}
            height={23}
            src="/icons/label-new.svg"
            alt="new tag"
            className={styles.tag}
            loading="lazy"
          />
        );
      }

      if (isPopular) {
        return (
          <Image
            width={42}
            height={23}
            src="/icons/label-top.svg"
            alt="popular tag"
            className={styles.tag}
            loading="lazy"
          />
        );
      }

      return null;
    }, [isNew, isPopular, isLimited, shouldLoad]);

    // Создаем объект для PromoLabel
    const promoItem: PromoItem = useMemo(
      () => ({
        __typename,
        id,
      }),
      [__typename, id],
    );

    const MediaComponent = useMemo(() => {
      if (!shouldLoad || !image) {
        return <div className={styles.placeholder} />;
      }

      if (isVideo) {
        return (
          <video
            key={image}
            ref={setVideoElementRef}
            preload="none"
            loop
            muted
            playsInline
            className={previewClasses}
            onLoadedData={handleMediaLoad}
            onError={handleMediaLoad}
            style={{
              opacity: mediaLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          >
            <source type="video/mp4" src={image} />
          </video>
        );
      }

      return (
        <Image
          width={152}
          height={152}
          src={image}
          alt={name || 'style preview'}
          className={previewClasses}
          priority={isPriority}
          loading={isPriority ? 'eager' : 'lazy'}
          quality={isPriority ? 85 : 75}
          onLoad={handleMediaLoad}
          onError={handleMediaLoad}
          placeholder="blur"
          blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSyKNZsP6h/VCyPcEWEJ3BvkZKJ8KzJhYzJZrTLrq+t6dPQgwLLgBBKLU1PKHkAIIJFAJKXLhWQCbVZKNZsP6h/VCyPcEWEJ3BvkZKJ8KzJhYzJZrTLrq+t6dPQgwLLgBBKLU1PKHkAIIJFAJKXLhWQCbVZKNZsP6h/VCyPcEWEJ3BvkZKJ8KzJhYzJZrTLrq+t6dPQgwLLgBBKLU1PKHkAIIJFAJKXLhWQCbVZKNZsP6h/VCyPcEWEJ3BvkZKJ8KzJhYzJZrTLrq+t6dPQgwLLgBBKLU1PKHkAIIJFAJKXLhWQCbVZKNZsP6h/VCyPcEWEJ3BvkZKJ8KzJhYzJZrTLrq+t6dPQgwLLgBBKLU1PKHkAIIJFAJKXLhWQCbVZKN/2Q=="
          style={{
            opacity: mediaLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        />
      );
    }, [
      shouldLoad,
      image,
      isVideo,
      previewClasses,
      handleMediaLoad,
      name,
      isPriority,
      mediaLoaded,
      setVideoElementRef,
    ]);

    return (
      <button
        ref={setElementRef}
        className={styles.styleItem}
        data-style-id={id}
        data-style-ind={index + 1}
        data-category-id={categoryId}
        onClick={handleClickImage}
        aria-label={`Select ${name} style`}
      >
        <div className={containerClasses}>
          {/* Skeleton loader */}
          {!mediaLoaded && shouldLoad && <div className={styles.skeleton} />}

          {/* Tags */}
          {TagComponent}

          {/* Promo Label */}
          {shouldLoad && <PromoLabel item={promoItem} />}

          {/* Limited Timer */}
          {isLimited && limitedUntil && (
            <LimitedTimer
              endDate={limitedUntil}
              setIsLeft30mins={handleTimerChange}
              setIsTimerLoaded={handleTimerLoad}
            />
          )}

          {/* Media Content */}
          {MediaComponent}

          {/* Gradient Overlay */}
          <div
            className={twMerge(
              styles.gradient,
              isActual && styles.gradientActual,
            )}
          />

          {/* Info Section */}
          <div className={styles.info}>
            <div className={styles.styleName}>{name}</div>
          </div>
        </div>
      </button>
    );
  },
);

PackPreview.displayName = 'PackPreview';
